
import React, { useState, useEffect } from 'react';
import { Code, ArrowRight, Play, CheckCircle, AlertTriangle } from 'lucide-react';
import { AppNode } from '../../../types';
import { ExpressionEngine } from '../../../utils/expressionEngine';

interface ScriptTaskSimulationFormProps {
  node: AppNode;
  onNext: (data: Record<string, unknown>) => void;
  variables: Record<string, unknown>; // Current variables context
}

export const ScriptTaskSimulationForm: React.FC<ScriptTaskSimulationFormProps> = ({ node, onNext, variables }) => {
  const [updatedVariables, setUpdatedVariables] = useState<Record<string, unknown>>({});
  const [logs, setLogs] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [executed, setExecuted] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    // Execute immediately safely
    executeScript();
  }, [node.id]);

  const executeScript = async () => {
    setIsRunning(true);
    setError(null);
    setLogs([]);
    setUpdatedVariables({});
    
    const script = node.data.script;
    if (!script) {
        setLogs(prev => [...prev, "No script content defined."]);
        setExecuted(true);
        return;
    }

    try {
        // We pass variables in for reading, but we expect an object back for writing
        const clonedVariables = JSON.parse(JSON.stringify(variables));
        const sandboxContext = { ...clonedVariables, input: clonedVariables, variables: clonedVariables };
        
        const result = await ExpressionEngine.execute(script, sandboxContext);

        // Validate return type
        if (result === undefined || result === null) {
             setLogs(prev => [...prev, "Script executed but returned no value."]);
             setExecuted(true);
             return;
        }

        if (typeof result !== 'object' || Array.isArray(result)) {
            throw new Error("Script must return an object (e.g., return { key: value }).");
        }

        // Merge result into updated variables
        // We do a shallow merge of the returned object with existing variables for the next step
        const newVars = { ...variables, ...result };
        
        setUpdatedVariables(newVars);
        setLogs(prev => [...prev, `Script returned ${Object.keys(result).length} fields.`]);
        setExecuted(true);

    } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : "Unknown error during script execution";
        setError(msg);
        setLogs(prev => [...prev, `Error: ${msg}`]);
    } finally {
        setIsRunning(false);
    }
  };

  const getChangedVariables = () => {
      const changes: Record<string, unknown> = {};
      
      // Calculate what changed based on the updatedVariables state vs the incoming props variables
      // Note: updatedVariables contains the FULL set (merged), so we check differences against `variables`
      Object.keys(updatedVariables).forEach(key => {
          if (JSON.stringify(updatedVariables[key]) !== JSON.stringify(variables[key])) {
              changes[key] = updatedVariables[key];
          }
      });
      
      return changes;
  };

  const changes = getChangedVariables();
  const hasChanges = Object.keys(changes).length > 0;

  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-900">
      <div className="flex-1 overflow-y-auto p-5">
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-2">
             <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider">
                <Code size={12} />
                <span>Execution</span>
             </div>
             <button 
                onClick={executeScript}
                className="text-[10px] flex items-center gap-1 text-teal-600 hover:text-teal-700 font-bold uppercase tracking-wider bg-teal-50 dark:bg-teal-900/20 px-2 py-1 rounded hover:bg-teal-100 transition-colors"
             >
                <Play size={10} fill="currentColor" /> {isRunning ? 'Running...' : 'Re-run'}
             </button>
          </div>

          <div className="relative">
             <pre className="text-[10px] font-mono bg-slate-50 dark:bg-slate-950 p-3 rounded-lg border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 overflow-x-auto max-h-40 whitespace-pre-wrap break-all">
                 {node.data.script || '// No script defined'}
             </pre>
          </div>

          <div className="space-y-2">
              <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Result</h5>
              
              {error ? (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900 rounded-lg text-xs text-red-600 flex items-start gap-2">
                      <AlertTriangle size={14} className="mt-0.5 shrink-0"/>
                      {error}
                  </div>
              ) : (
                  <div className="p-3 bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-800 rounded-lg space-y-2">
                      {logs.map((log, i) => (
                          <div key={i} className="text-xs text-slate-500 font-mono flex items-center gap-2">
                              <CheckCircle size={10} className="text-emerald-500"/> {log}
                          </div>
                      ))}
                      
                      {hasChanges && (
                          <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-800">
                              <div className="text-[10px] font-bold text-slate-400 mb-2">OUTPUT VARIABLES</div>
                              <div className="space-y-1.5">
                                  {Object.entries(changes).map(([key, val]) => (
                                      <div key={key} className="flex flex-col gap-0.5 text-xs font-mono bg-white dark:bg-slate-900 p-1.5 rounded border border-slate-200 dark:border-slate-800">
                                          <span className="text-teal-600 font-bold">{key}</span>
                                          <span className="text-slate-500 dark:text-slate-400 break-all">{JSON.stringify(val)}</span>
                                      </div>
                                  ))}
                              </div>
                          </div>
                      )}
                  </div>
              )}
          </div>
        </div>
      </div>

      <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 flex justify-end">
        <button 
          onClick={() => onNext(updatedVariables)}
          disabled={!!error || !executed || isRunning}
          className="w-full px-5 py-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-bold flex items-center justify-center gap-2 shadow-sm transition-all hover:translate-y-px text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next Step
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};
